import { DatePipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyOptionModule } from '@angular/material/legacy-core';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorDirective, ErrorsDirective } from '@shared/directives/errors';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { UtilService } from '@shared/services/util.service';
import { IMemberProfileSpecialOccasion } from '@shared/shared/interfaces/member-profile-special-occasion.interfaces';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import * as MemberProfileStore from '@shared/store/member-profile';
import { Angulartics2Segment } from 'angulartics2';

@Component({
  selector: 'ten-special-occasion-form',
  templateUrl: './special-occasion-form.component.html',
  styleUrls: ['../popup-form-base.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    NgIf,
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyOptionModule,
    ErrorDirective,
    ErrorsDirective,
    MatLegacyInputModule,
    MatDatepickerModule,
    MatLegacyButtonModule,
    NgClass,
  ],
})
export class SpecialOccasionFormComponent implements OnInit, OnDestroy {
  @Input() public memberUrl: string;
  @Input() public pastDataId?: any;
  @Output() public closed = new EventEmitter<any>();

  public member_profile_special_occasions$: Observable<IMemberProfileSpecialOccasion[]> = this.ngrxStore.select(
    MemberProfileStore.getMemberProfileSpecialOccasionsFeatureState
  );

  public newSpecialOccasion: boolean;
  public specialOccasionForm: UntypedFormGroup;
  private unsubscribe$ = new Subject<void>();
  public innerWidth: number;
  public language: string;
  public country: string;
  public client: string;

  constructor(
    private form_builder: UntypedFormBuilder,
    private datePipe: DatePipe,
    private ngrxStore: Store<MemberProfileStore.MemberProfileState>,
    private angulartics2Segment: Angulartics2Segment,
    private util: UtilService,
    private siteSettingsService: SiteSettingsService
  ) {
    this.innerWidth = window.innerWidth;
    this.specialOccasionForm = this.form_builder.group({
      type: ['', Validators.required],
      occasion_name: ['', [Validators.required, Validators.pattern(/^[^;<>()*]/i)]],
      date: ['', Validators.required],
    });
  }

  public ngOnInit() {
    this.newSpecialOccasion = this.pastDataId === undefined;
    if (!this.newSpecialOccasion) {
      this.member_profile_special_occasions$
        .pipe(
          filter((value) => value && value !== null),
          take(1),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((data) => {
          data.forEach((value) => {
            if (value.id === this.pastDataId) {
              this.restoreFormData(value);
            }
          });
        });
    }

    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((siteSettings: ISiteSettings) => !!siteSettings.id),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.language = siteSettings.language;
        this.country = siteSettings.country_code;
        this.client = siteSettings.fe_configuration.name;
      });
  }

  /**
   * Dispatch the relevant action to delete the current special occasion. Called when the user clicks onto the
   * remove button.
   */
  public removeSpecialOccasion() {
    this.ngrxStore.dispatch(new MemberProfileStore.DestroyProfileSpecialOccasion(this.pastDataId));
    this.closeForm();
  }

  /**
   * Sets the value of the form fields if form is used to edit a previously saved special occasion. Called from
   * the 'obtainSpecialOccasionData' function
   * @param data - Special occasion data to be edited
   */
  public restoreFormData(data: IMemberProfileSpecialOccasion) {
    this.specialOccasionForm.get('type').setValue(data.type);
    this.specialOccasionForm.get('occasion_name').setValue(data.occasion_name);
    this.specialOccasionForm.get('date').setValue(`${data.date}T12:00:00.000Z`);
  }

  /**
   * Close the special occasion form by emitting a value that is caught by the popupFormComponent. Called when the user
   * clicks onto the close button.
   */
  public closeForm() {
    this.closed.emit();
  }

  /**
   * Checks for form validation before dispatching the relevant action to either add or update a special occasion.
   * Called when the user clicks onto the submit button.
   */
  public submitForm() {
    const data = this.specialOccasionForm.value;
    data.date = this.datePipe.transform(data.date, 'yyyy-MM-dd');
    this.util.stripHTMLFromObject(data);
    if (this.specialOccasionForm.valid) {
      this.angulartics2Segment.eventTrack('My profile special occasion submitted', {
        client: this.client,
        country: this.country,
        language: this.language,
        type: data.type,
      });

      if (this.newSpecialOccasion) {
        data.member_profile = this.memberUrl;
        this.ngrxStore.dispatch(new MemberProfileStore.CreateProfileSpecialOccasion(data));
      } else {
        this.ngrxStore.dispatch(new MemberProfileStore.UpdateProfileSpecialOccasion({ id: this.pastDataId, data }));
      }
      this.closeForm();
    } else {
      Object.keys(this.specialOccasionForm.controls).forEach((field) => {
        this.specialOccasionForm.controls[field].markAsTouched();
      });
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:resize', ['$event'])
  public onResize() {
    this.innerWidth = window.innerWidth;
  }
}
